/**
 * Type-safe version of Object.keys() that preserves the object's key types.
 */
export function objectKeys<T extends object>(obj: T): Array<keyof T> {
  return Object.keys(obj) as Array<keyof T>;
}

/**
 * Type-safe version of Object.entries() that preserves the object's key and value types.
 */
export function objectEntries<T extends object>(
  obj: T,
): Array<[keyof T, T[keyof T]]> {
  return Object.entries(obj) as Array<[keyof T, T[keyof T]]>;
}

/**
 * Maps over an object's values while preserving its key structure.
 * Similar to Array.map() but for objects.
 */
export function objectMap<T extends object, U>(
  obj: T,
  fn: (value: T[keyof T], key: keyof T) => U,
): Record<keyof T, U> {
  return objectKeys(obj).reduce(
    (acc, key) => {
      acc[key] = fn(obj[key], key);
      return acc;
    },
    {} as Record<keyof T, U>,
  );
}

/**
 * Creates a new object containing only the specified keys from the source object.
 * Useful for extracting a subset of properties from an object.
 */
export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): Pick<T, K> {
  return keys.reduce(
    (acc, key) => {
      acc[key] = obj[key];
      return acc;
    },
    {} as Pick<T, K>,
  );
}

/**
 * Groups array items by a key function, returning an array of [key, items] tuples.
 * Useful for organizing data by categories or properties.
 */
export function grouped<T, K extends string = string>(
  arr: ReadonlyArray<T>,
  getKey: (item: T) => K,
): [K, T[]][] {
  return objectEntries(
    arr.reduce(
      (acc, item) => {
        const key = getKey(item);
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      },
      {} as Record<K, T[]>,
    ),
  );
}

/**
 * Returns all methods of an object, including those inherited from its prototype chain.
 * This is useful for introspection or debugging purposes.
 */
export function getAllMethods(entity: object): string[] {
  const methods: string[] = [];
  let current = entity;

  // Traverse up the prototype chain
  while (
    current !== null &&
    current !== undefined &&
    current !== Object.prototype
  ) {
    // Get own property names of the current object
    const propertyNames = Object.getOwnPropertyNames(current) as [
      keyof typeof current,
    ];

    // Filter out non-function properties and already added methods
    for (const name of propertyNames) {
      // Access on entity and not currentObject because we're looking at prototypes
      if (typeof entity[name] === "function" && !methods.includes(name)) {
        methods.push(name);
      }
    }

    // Move up the prototype chain
    current = Object.getPrototypeOf(current);
  }

  return methods;
}
